import { adminActionButtonSx } from 'src/theme/adminActionButton';
import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  Button,
  Tooltip,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type ShiftUser = {
  isRemoved?: boolean; // optional flag if user is already removed
  user: {
    firstName: string;
    lastName: string;
    email: string;
    id?: string | number; // <-- the ID we want to return
  };
  id?: string | number; // optional record id
  leadUser?: number
};

type Props = {
  open: boolean;
  onClose: () => void;
  usersData: ShiftUser[];
  /** Called with array of removed user IDs */
  onSave?: (removedIds: Array<string | number>) => void;
  zIndexOffset?: number;
  /** Number of users to remove (selection cap) */
  usersToRemoveQuantity: number;
  leadUser?: number
};

// consistent key to track selection in UI
const getKey = (u: ShiftUser, idx: number) =>
  (u.user?.id ?? u.id ?? `${u.user?.email}-${idx}`) as string | number;

// the ID we actually return to API/parent
const getUserId = (u: ShiftUser, idx: number) =>
  ( u.id ?? `${u.user?.email}-${idx}`) as string | number;

const ShiftUsersDrawer: React.FC<Props> = ({
  open,
  onClose,
  usersData = [],
  onSave,
  zIndexOffset = 10,
  usersToRemoveQuantity,
  leadUser,
}) => {
  const [selectedKeys, setSelectedKeys] = React.useState<Set<string | number>>(
    new Set()
  );

  console.log('userData', usersData);

  // Reset selection when drawer opens
  React.useEffect(() => {
    if (open) setSelectedKeys(new Set());
  }, [open]);



  const selectedCount = selectedKeys.size;
  const remaining = Math.max(usersToRemoveQuantity - selectedCount, 0);
  const canPickMore = selectedCount < usersToRemoveQuantity;
  const canSave =
    usersToRemoveQuantity > 0 && selectedCount === usersToRemoveQuantity;


    

  const toggleOne = (key: string | number) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        if (!canPickMore) return prev; // block extra picks
        next.add(key);
      }
      return next;
    });
  };

  const handleSave = () => {
    if (!canSave) return;
    // Return just the user IDs in the same order as usersData
    const removedIds = usersData
      .map((u, idx) => ({ id: getUserId(u, idx), key: getKey(u, idx) }))
      .filter(({ key }) => selectedKeys.has(key))
      .map(({ id }) => id);
    console.log('removedIds', removedIds);
    onSave?.(removedIds);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + zIndexOffset }}
      ModalProps={{
        keepMounted: true,
        hideBackdrop: true,
        slotProps: { backdrop: { sx: { backgroundColor: 'transparent' } } }
      }}
      PaperProps={{
        sx: {
          width: 420,
          maxWidth: '90vw',
          borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
          boxShadow: 24
        }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5">Shift Users</Typography>
          <Typography variant="body2">
            Select <b>{usersToRemoveQuantity}</b> user
            {usersToRemoveQuantity === 1 ? '' : 's'} to send to extras. &nbsp;Remaining:{' '}
            <b>{remaining}</b>
          </Typography>
        </Stack>
        <IconButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      <Box sx={{ p: 2, pt: 0 }}>
        <List dense sx={{ pb: 0 }}>
          {usersData?.length ? (
            usersData.map((u, idx) => {
              const key = getKey(u, idx);
              console.log('U', u);
              const isNotBooked = !u.user;
              const label = isNotBooked
                ? 'Not Booked'
                : `${u.user.firstName} ${u.user.lastName}`;

              const secondary = isNotBooked ? '' : u.user.email;
              const isRemoved = u?.isRemoved;
              const checked = selectedKeys.has(key);
              const isLeadUser = !isNotBooked && u.user.id === leadUser;
              // Disable if: lead user, or limit reached and not already checked
              const disableThis = isLeadUser ? true : (!checked && !canPickMore);
              
              const tooltipMessage = isLeadUser
                ? 'You cannot remove this user because this is the lead. First remove them from lead.'
                : disableThis
                ? `You've reached the limit (${usersToRemoveQuantity})`
                : '';
              
              return (
                <ListItem key={String(key)} disablePadding>
                  <Tooltip
                    title={tooltipMessage}
                    arrow
                    disableHoverListener={!tooltipMessage}
                  >
                    <span>
                      <ListItemButton
                        onClick={() => toggleOne(key)}
                        disabled={disableThis}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={checked}
                            tabIndex={-1}
                            disableRipple
                           disabled={disableThis}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <div className="flex items-center gap-2">
                              <span>{label}</span>
                              {!isNotBooked && u.user.id === leadUser && (
                                <Chip
                                  label="LEAD"
                                  color="success"
                                  size="small"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                              {isRemoved && (
                                <Chip
                                  label="Removed"
                                  color="error"
                                  size="small"
                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                />
                              )}
                            </div>
                          }
                          secondary={secondary}
                        />
                      </ListItemButton>
                    </span>
                  </Tooltip>
                </ListItem>
              );
            })
          ) : (
            <Typography variant="body2" sx={{ p: 1 }}>
              No users found.
            </Typography>
          )}
        </List>
      </Box>

      <Box sx={{ mt: 'auto', p: 2 }}>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="outlined"
            sx={{
              width: 100,
              borderColor: '#E8BF21',
              borderWidth: 2,
              color: '#000000',
              '&:hover': {
                borderColor: '#E8BF21',
                borderWidth: 2,
                backgroundColor: 'none'
              }
            }}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              width: 100,
              ...adminActionButtonSx
            }}
            onClick={handleSave}
            disabled={!canSave}
          >
            Save
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default ShiftUsersDrawer;
